import * as React from "react"
import { ChartData } from "../subsection"
import { ChartType } from "../../snapshot/analysis_panel"
import { GeneratePieChart } from "./pie"
import { GenerateHistogram } from "./histogram"
import { GenerateBarChart } from "./bar"
import { GenerateTableRowMeanChart } from "./table_row_mean"
import { TileGridProps } from "../../modelling/tile_grid"
import type { TableMeanChartSettings, TableMeanCompareOption } from "./table_row_mean"

interface ChartProps {
    chartData: ChartData
    chartType: ChartType
    props: TileGridProps | undefined
    cellArea: number
    hideNoDataValues?: boolean
    /** Narrow sidebar / embed: smaller SVGs and margins */
    compact?: boolean
    panelWidth?: number
    panelHeight?: number
    svgId?: string
    tableMeanCompareOptions?: TableMeanCompareOption[]
    tableMeanCompareLayerId?: number | null
    onTableMeanCompareLayerIdChange?: (layerId: number | null) => void
    tableMeanCompareChartData?: ChartData
    tableMeanOverlayLayerIds?: number[]
    onTableMeanOverlayLayerIdsChange?: (layerIds: number[]) => void
    tableMeanOverlayChartDataByLayerId?: Record<number, ChartData>
    tableMeanPrimaryLayerName?: string
    tableMeanSettings?: TableMeanChartSettings
    onTableMeanSettingsChange?: (patch: Partial<TableMeanChartSettings>) => void
}
export const GenerateChart = ({
    chartData,
    chartType,
    props,
    cellArea,
    hideNoDataValues,
    compact,
    panelWidth,
    panelHeight,
    svgId,
    tableMeanCompareOptions,
    tableMeanCompareLayerId,
    onTableMeanCompareLayerIdChange,
    tableMeanCompareChartData,
    tableMeanOverlayLayerIds,
    onTableMeanOverlayLayerIdsChange,
    tableMeanOverlayChartDataByLayerId,
    tableMeanPrimaryLayerName,
    tableMeanSettings,
    onTableMeanSettingsChange,
}: ChartProps) => {
    
    const dataForCharts: ChartData = React.useMemo(() => {
        if (!hideNoDataValues) return chartData

        const isNoData = (key: any) => typeof key === "string" && key.trim().toLowerCase() === "no data"

        const filteredCount = new Map(
            Array.from(chartData.count.entries()).filter(([key]) => !isNoData(key))
        )

        const filteredColors = new Map(
            Array.from(chartData.colors.entries()).filter(([key]) => !isNoData(key))
        )

        return {
            ...chartData,
            count: filteredCount,
            colors: filteredColors
        }
    }, [chartData, hideNoDataValues])


    switch (chartType) {
        case "pie":
            return <GeneratePieChart
                chartData={dataForCharts}
                compact={compact}
                panelWidth={panelWidth}
                panelHeight={panelHeight}
                svgId={svgId}
            />
            break;
        case "hist":
            return <GenerateHistogram
                chartData={dataForCharts}
                props={props}
                cellArea={cellArea}
                compact={compact}
                panelWidth={panelWidth}
                panelHeight={panelHeight}
                svgId={svgId}
            />
            break;
        case "bar":
            return <GenerateBarChart
                chartData={dataForCharts}
                compact={compact}
                panelWidth={panelWidth}
                panelHeight={panelHeight}
                svgId={svgId}
            />
            break;
        case "tableMean":
            return <GenerateTableRowMeanChart
                chartData={chartData}
                compact={compact}
                panelWidth={panelWidth}
                panelHeight={panelHeight}
                svgId={svgId ?? "tableMean"}
                variant="bars"
                compareOptions={tableMeanCompareOptions}
                compareLayerId={tableMeanCompareLayerId}
                onCompareLayerIdChange={onTableMeanCompareLayerIdChange}
                compareChartData={tableMeanCompareChartData}
                primaryLayerName={tableMeanPrimaryLayerName}
                settings={tableMeanSettings}
                onSettingsChange={onTableMeanSettingsChange}
            />
            break;
        case "tableSpider":
            return <GenerateTableRowMeanChart
                chartData={chartData}
                compact={compact}
                panelWidth={panelWidth}
                panelHeight={panelHeight}
                svgId={svgId ?? "tableSpider"}
                variant="spider"
                compareOptions={tableMeanCompareOptions}
                overlayLayerIds={tableMeanOverlayLayerIds}
                onOverlayLayerIdsChange={onTableMeanOverlayLayerIdsChange}
                overlayChartDataByLayerId={tableMeanOverlayChartDataByLayerId}
                primaryLayerName={tableMeanPrimaryLayerName}
                settings={tableMeanSettings}
                onSettingsChange={onTableMeanSettingsChange}
            />
            break;
        default:
            return <>Error</>
            break;
    }

}

